import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';

import PropertyDetailWrapper from '~components/property-detail-wrapper';
import PriceCalcDisplaySelect from 'common/components/listing-management/price-calc-display/price-calc-display-select';
import {
  DailyPriceCalcConvertedAnnual,
  DailyPriceCalcConvertedWeekly,
  DailyPriceCalcFixedDaysInMonth,
  DailyPriceCalcSpecificCalendarMonth,
  MonthPriceCalcSpecificAdvanced,
  MonthPriceCalcSpecificBasic,
} from '~components/svgs';
import { DailyPriceTypes } from 'common/constants/price-calc-display';
import {
  getMoveCategory,
  getMoveType,
  getMoveDate,
} from 'common/components/listing-management/price-calc-display/helper';

export const DailyPriceCalculationOptions = {
  [DailyPriceTypes.CUSTOM_LENGTH]: {
    label:
      'cms:cms.property.listing_management.price-calc-display.daily_price_type.custom_length.label',
    des: 'cms:cms.property.listing_management.price-calc-display.daily_price_type.custom_length.des',
    icon: <DailyPriceCalcFixedDaysInMonth />,

    extraDes: [
      ({ editingPriceCalculationRule }) =>
        `Days in a month: ${editingPriceCalculationRule.customLength}`,
      ({ editingPriceCalculationRule }) =>
        `Daily price = monthly price / ${editingPriceCalculationRule.customLength}`,
    ],
  },
  [DailyPriceTypes.CALENDAR_MONTH_DAYS]: {
    label:
      'cms:cms.property.listing_management.price-calc-display.daily_price_type.calendar_month_days.label',
    des: 'cms:cms.property.listing_management.price-calc-display.daily_price_type.calendar_month_days.des',
    icon: <DailyPriceCalcSpecificCalendarMonth />,
    extraDes: [
      'cms:cms.property.listing_management.price-calc-display.daily_price_type.calendar_month_days.extra_des.1',
    ],
  },
  CONVERTED_WEEKLY: {
    label:
      'cms:cms.property.listing_management.price-calc-display.daily_price_type.converted_weekly.label',
    des: 'cms:cms.property.listing_management.price-calc-display.daily_price_type.converted_weekly.des',
    icon: <DailyPriceCalcConvertedWeekly />,
    extraDes: [],
  },
  CONVERTED_ANNUAL: {
    label:
      'cms:cms.property.listing_management.price-calc-display.daily_price_type.converted_annual.label',
    des: 'cms:cms.property.listing_management.price-calc-display.daily_price_type.converted_annual.des',
    icon: <DailyPriceCalcConvertedAnnual />,
    extraDes: [
      ({ editingPriceCalculationRule }) =>
        `Days in a year: ${editingPriceCalculationRule.customLength}`,
      ({ editingPriceCalculationRule }) =>
        `Daily price = monthly price*12 / ${editingPriceCalculationRule.customLength}`,
    ],
  },
};

export const FinalPriceCalculationOptions = {
  BASIC_SETTING: {
    label:
      'cms:cms.property.listing_management.price-calc-display.final_price_calculation_option.basic.label',
    des: 'cms:cms.property.listing_management.price-calc-display.final_price_calculation_option.basic.des',
    icon: <MonthPriceCalcSpecificBasic />,
    extraDes: [
      'cms:cms.property.listing_management.price-calc-display.final_price_calculation_option.basic.extra_des.1',
    ],
  },
  ADVANCED_SETTING: {
    label:
      'cms:cms.property.listing_management.price-calc-display.final_price_calculation_option.advanced.label',
    des: 'cms:cms.property.listing_management.price-calc-display.final_price_calculation_option.advanced.des',
    icon: <MonthPriceCalcSpecificAdvanced />,
    extraDes: [
      [
        ({ editingPriceCalculationRule }) =>
          getMoveCategory('moveIn', editingPriceCalculationRule.moveInType),
        ({ editingPriceCalculationRule }) =>
          getMoveType('moveIn', editingPriceCalculationRule.moveInType),
        ({ editingPriceCalculationRule }) =>
          getMoveDate(
            editingPriceCalculationRule.moveInType,
            editingPriceCalculationRule.moveInStart,
            editingPriceCalculationRule.moveInEnd,
          ),
      ],

      [
        ({ editingPriceCalculationRule }) =>
          getMoveCategory('moveOut', editingPriceCalculationRule.moveOutType),
        ({ editingPriceCalculationRule }) =>
          getMoveType('moveOut', editingPriceCalculationRule.moveOutType),
        ({ editingPriceCalculationRule }) =>
          getMoveDate(
            editingPriceCalculationRule.moveOutType,
            editingPriceCalculationRule.moveOutStart,
            editingPriceCalculationRule.moveOutEnd,
          ),
      ],

      [
        'cms:cms.property.listing_management.price-calc-display.final_price_calculation_option.advanced.extra_des.7',
      ],
    ],
    extraDesArea: [
      'A month is defined as a full calendar month i.e. If the stay is for all 31 days in Jan, all 28/29 days in Feb, all 31 days in Mar, etc',
      '<b>Charge the remaining days as a full month</b> means no matter how many days the student stays in the corresponding month, charge the remaining days as a full monthly price. These advanced settings decide how to calculate the price of the days that are not part of a full calendar month:',
      '<div><ul><li>Move in earlier than the date</li><li>Move out later than the date</li><li>Move in/out within a date range</li><li>Move in/out no limit</li></ul></div>',
      '<b>Charge the remaining days according to actual days of occupancy in the corresponding month</b> means when calculating the price for the remaining days, get the daily price from the daily price configuration and then use the daily price times remaining days.',
    ],
  },
};

export function PriceCalcDisplay({ t, property, afterSave }) {
  const { priceCalculationRuleSetting, billingCycle } = property;
  const useMonthly = billingCycle === 'MONTHLY';
  const defaultPriceCalculationRuleTemplate = useMonthly
    ? priceCalculationRuleSetting.allBillingCycleRule.monthly
    : priceCalculationRuleSetting.allBillingCycleRule.weekly;

  const { t: cmsT } = useTranslation('cms');

  const [editingPriceCalculationRule] = useState(() => {
    if (priceCalculationRuleSetting?.priceCalculationRule) {
      return priceCalculationRuleSetting.priceCalculationRule;
    } else {
      return {
        calculateMethod: 'BASIC',
        ...defaultPriceCalculationRuleTemplate,
      };
    }
  });

  async function handleSave() {
    return new Promise(resolve => {
      resolve({ status: 'success', isUpdated: false });
      afterSave(property);
    });
  }

  return (
    <PropertyDetailWrapper
      property={property}
      t={t}
      isPublished={property.status === 'PUBLISHED'}
      onClickSave={handleSave}
      hiddenSubmit
    >
      <div className="price-calc-display">
        <Alert
          className="price-calc-display__alert"
          message={t('cms:cms.property.listing_management.price-calc-display.alert')}
          type="info"
          showIcon
        />

        <div className="price-calc-display__setting-block">
          <h2 className="property-detail__subtitle">
            {t('cms:cms.property.listing_management.price-calc-display.daily_price_calc_title')}
          </h2>
          <p className="price-calc-display__setting-block__des">
            {t('cms:cms.property.listing_management.price-calc-display.daily_price_calc_des')}
          </p>

          <PriceCalcDisplaySelect.SelectedOption
            key="daily-selected-option"
            option={
              DailyPriceCalculationOptions[
                editingPriceCalculationRule?.dailyPriceType || DailyPriceTypes.CALENDAR_MONTH_DAYS
              ]
            }
            editingPriceCalculationRule={editingPriceCalculationRule}
            t={cmsT}
          />

          <PriceCalcDisplaySelect.OtherOptions
            key="daily-other-option"
            options={Object.keys(DailyPriceTypes)
              .filter(o => o !== editingPriceCalculationRule?.dailyPriceType)
              .map(o => DailyPriceCalculationOptions[o])}
            t={cmsT}
          />
        </div>

        <div className="price-calc-display__setting-block">
          <h2 className="property-detail__subtitle">
            {t('cms:cms.property.listing_management.price-calc-display.final_price_calc_title', {
              cycle: 'Monthly',
            })}
          </h2>
          <p className="price-calc-display__setting-block__des">
            {t('cms:cms.property.listing_management.price-calc-display.final_price_calc_des')}
          </p>

          <PriceCalcDisplaySelect.SelectedOption
            key="final-selected-option"
            displayExtraDesArea
            option={
              FinalPriceCalculationOptions[
                editingPriceCalculationRule?.calculateMethod
                  ? `${editingPriceCalculationRule.calculateMethod}_SETTING`
                  : 'BASIC_SETTING'
              ]
            }
            editingPriceCalculationRule={editingPriceCalculationRule}
            t={cmsT}
          />

          <PriceCalcDisplaySelect.OtherOptions
            key="final-other-option"
            displayExtraDesArea
            options={Object.keys(FinalPriceCalculationOptions)
              .filter(
                o => o.replace('_SETTING', '') !== editingPriceCalculationRule?.calculateMethod,
              )
              .map(o => FinalPriceCalculationOptions[o])}
            t={cmsT}
          />
        </div>
      </div>
    </PropertyDetailWrapper>
  );
}
